<script setup>
// V1 Admin Intelligence Bridge (Part 6) — read/retry/cancel surface over the
// existing Nexceo::WorkforceTask + Nexceo::Ai::WorkforceTaskQueue (M26).
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import workforceTasksAPI from 'dashboard/api/nexceo/workforceTasks';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const tasks = ref([]);
const meta = ref({ page: 1, per_page: 25, total: 0, total_pages: 1 });
const isLoading = ref(false);
const error = ref('');
const statusFilter = ref('');
const capabilityFilter = ref('');
const busyId = ref(null);

// Nexceo::WorkforceTask::STATUSES — cancel! reuses 'failed' (with a
// 'Cancelled by admin' error_message) rather than a distinct state, so
// there is no separate 'cancelled' status to filter on.
const STATUSES = ['pending', 'running', 'completed', 'failed'];

async function load(page = 1) {
  isLoading.value = true;
  error.value = '';
  try {
    const params = { page };
    if (statusFilter.value) params.status = statusFilter.value;
    if (capabilityFilter.value) params.capability = capabilityFilter.value;
    const { data } = await workforceTasksAPI.get(params);
    tasks.value = data.tasks || [];
    meta.value = data.meta || meta.value;
  } catch {
    error.value = 'Failed to load workforce tasks.';
  } finally {
    isLoading.value = false;
  }
}

watch([statusFilter, capabilityFilter], () => load(1));

async function retryTask(task) {
  busyId.value = task.id;
  try {
    await workforceTasksAPI.retry(task.id);
    await load(meta.value.page);
  } catch {
    error.value = `Failed to retry task #${task.id}.`;
  } finally {
    busyId.value = null;
  }
}

async function cancelTask(task) {
  busyId.value = task.id;
  try {
    await workforceTasksAPI.cancel(task.id);
    await load(meta.value.page);
  } catch {
    error.value = `Failed to cancel task #${task.id}.`;
  } finally {
    busyId.value = null;
  }
}

function statusBadgeClass(status) {
  return (
    {
      completed:
        'bg-success-50 text-success-800 border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30',
      failed:
        'bg-ruby-50 text-ruby-700 border-ruby-200 dark:bg-ruby-500/10 dark:text-ruby-400 dark:border-ruby-500/30',
      running:
        'bg-woot-50 text-woot-700 border-woot-200 dark:bg-woot-500/10 dark:text-woot-400 dark:border-woot-500/30',
      pending:
        'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30',
    }[status] || 'bg-n-alpha-2 text-n-slate-8 border-n-weak'
  );
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString();
}

function formatKey(key) {
  return (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function back() {
  router.push(accountScopedRoute('nexceo_ai_workforce'));
}

const hasPrev = computed(() => meta.value.page > 1);
const hasNext = computed(() => meta.value.page < meta.value.total_pages);

onMounted(() => load(1));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
        @click="back"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <span class="i-lucide-list-todo size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">Workforce Tasks</h1>
      <span
        class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9"
      >
        {{ meta.total }}
      </span>

      <div class="ml-auto flex items-center gap-2">
        <select
          v-model="statusFilter"
          class="appearance-none text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 cursor-pointer"
        >
          <option value="">All statuses</option>
          <option v-for="s in STATUSES" :key="s" :value="s">
            {{ s }}
          </option>
        </select>
        <input
          v-model="capabilityFilter"
          type="text"
          placeholder="Filter by capability…"
          class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-44"
        />
        <button
          :disabled="isLoading"
          class="flex items-center justify-center w-8 h-8 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:opacity-40"
          title="Refresh"
          @click="load(meta.page)"
        >
          <span
            :class="isLoading ? 'animate-spin' : ''"
            class="i-lucide-refresh-cw size-4"
          />
        </button>
      </div>
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="flex items-center gap-2 px-4 py-2 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 dark:border-ruby-500/30 text-sm text-ruby-700 dark:text-ruby-400 shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ error }}
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading && !tasks.length"
      class="flex-1 flex items-center justify-center"
    >
      <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
    </div>

    <!-- Empty -->
    <div
      v-else-if="!tasks.length"
      class="flex-1 flex flex-col items-center justify-center gap-4"
    >
      <div
        class="flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
      >
        <span class="i-lucide-list-todo size-7 text-woot-500" />
      </div>
      <div class="text-center">
        <p class="text-base font-semibold text-n-slate-12">
          {{
            statusFilter || capabilityFilter
              ? 'No matching tasks'
              : 'No workforce tasks yet'
          }}
        </p>
        <p class="text-sm text-n-slate-9 mt-1">
          {{
            statusFilter || capabilityFilter
              ? 'Try clearing your filters.'
              : 'Tasks appear here as AI employees hand off work to specialist capabilities.'
          }}
        </p>
      </div>
    </div>

    <!-- Table -->
    <div v-else class="flex-1 overflow-auto p-4">
      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr
              class="text-left text-xs text-n-slate-7 uppercase tracking-wider border-b border-n-weak"
            >
              <th class="px-3 py-2">Task</th>
              <th class="px-3 py-2">Capability</th>
              <th class="px-3 py-2">Lead</th>
              <th class="px-3 py-2">Project</th>
              <th class="px-3 py-2">Status</th>
              <th class="px-3 py-2">Created</th>
              <th class="px-3 py-2">Completed</th>
              <th class="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="task in tasks"
              :key="task.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
            >
              <td class="px-3 py-2 text-n-slate-12 font-medium">
                {{ formatKey(task.task_type) }}
              </td>
              <td class="px-3 py-2 text-n-slate-9">
                {{ formatKey(task.capability) }}
              </td>
              <td class="px-3 py-2 text-n-slate-9">
                {{ task.lead_name || '—' }}
              </td>
              <td class="px-3 py-2 text-n-slate-9">
                {{ task.project_name || '—' }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border capitalize"
                  :class="statusBadgeClass(task.status)"
                >
                  {{ task.status }}
                </span>
                <p
                  v-if="task.error_message"
                  class="text-xs text-ruby-600 dark:text-ruby-400 mt-0.5 max-w-xs truncate"
                  :title="task.error_message"
                >
                  {{ task.error_message }}
                </p>
              </td>
              <td class="px-3 py-2 text-n-slate-7 text-xs">
                {{ formatDate(task.created_at) }}
              </td>
              <td class="px-3 py-2 text-n-slate-7 text-xs">
                {{ formatDate(task.completed_at) }}
              </td>
              <td class="px-3 py-2 text-right whitespace-nowrap">
                <button
                  v-if="task.status === 'failed'"
                  :disabled="busyId === task.id"
                  class="text-xs px-2 py-1 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 disabled:opacity-40 mr-1"
                  @click="retryTask(task)"
                >
                  <span class="i-lucide-rotate-cw size-3 inline-block mr-0.5" />
                  Retry
                </button>
                <button
                  v-if="['pending', 'running'].includes(task.status)"
                  :disabled="busyId === task.id"
                  class="text-xs px-2 py-1 rounded-lg border border-n-weak text-n-slate-9 hover:bg-ruby-50 hover:text-ruby-600 dark:hover:bg-ruby-500/10 disabled:opacity-40"
                  @click="cancelTask(task)"
                >
                  <span class="i-lucide-x size-3 inline-block mr-0.5" />
                  Cancel
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between mt-3">
        <p class="text-xs text-n-slate-7">
          Page {{ meta.page }} of {{ meta.total_pages || 1 }}
        </p>
        <div class="flex items-center gap-2">
          <button
            :disabled="!hasPrev"
            class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 disabled:opacity-40"
            @click="load(meta.page - 1)"
          >
            Previous
          </button>
          <button
            :disabled="!hasNext"
            class="text-xs px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 disabled:opacity-40"
            @click="load(meta.page + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
